import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { QUOTES, shuffle, quotesByPack, type PackId } from "@/data/quotes";
import { loadBest, saveBest, loadStreak, saveStreak, streakMultiplier } from "@/lib/game-utils";

const ROUND_TIME = 60; // total session seconds
const TOTAL_CALLS = 999; // unbounded — score by calls

interface Claim { quoteId: string; claimedAuthor: string; claimedText: string; isTrue: boolean; }

function makeClaim(pack: PackId): Claim {
  const pool = quotesByPack(pack);
  const source = pool.length >= 8 ? pool : QUOTES;
  const q = source[Math.floor(Math.random() * source.length)];
  // 50% true, 25% wrong author, 25% wrong text (when available)
  const roll = Math.random();
  if (roll < 0.5) {
    return { quoteId: q.id, claimedAuthor: q.author, claimedText: q.text, isTrue: true };
  }
  if (roll < 0.75 || !q.fake) {
    // Wrong author: prefer q.fakeAuthor, otherwise random other author
    let wrong = q.fakeAuthor;
    if (!wrong) {
      const others = shuffle(QUOTES.filter((x) => x.author !== q.author));
      wrong = others[0]?.author ?? "Anonymous";
    }
    return { quoteId: q.id, claimedAuthor: wrong, claimedText: q.text, isTrue: false };
  }
  // Wrong text
  return { quoteId: q.id, claimedAuthor: q.author, claimedText: q.fake!, isTrue: false };
}

export function TrueOrFalseGame({ pack = "all" }: { pack?: PackId }) {
  const live = useLiveMode();
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [best, setBest] = useState(0);
  const [claim, setClaim] = useState<Claim>(() => makeClaim(pack));
  const [flash, setFlash] = useState<"right" | "wrong" | null>(null);
  const [calls, setCalls] = useState(0);
  const [remaining, setRemaining] = useState(ROUND_TIME);
  const [done, setDone] = useState(false);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    setBest(loadBest("true-or-false"));
    setBestStreak(loadStreak("true-or-false"));
  }, []);

  useEffect(() => {
    if (done) return;
    const id = setInterval(() => {
      const left = Math.max(0, ROUND_TIME - Math.floor((Date.now() - startRef.current) / 1000));
      setRemaining(left);
      if (left === 0) {
        setBest(saveBest("true-or-false", score));
        setDone(true);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [done, score]);

  const answer = (guess: boolean) => {
    if (done) return;
    const correct = guess === claim.isTrue;
    if (correct) {
      const mult = streakMultiplier(streak);
      setScore((s) => s + 1 * mult);
      setStreak((s) => {
        const n = s + 1;
        if (n > bestStreak) setBestStreak(saveStreak("true-or-false", n));
        return n;
      });
      setFlash("right");
    } else {
      setStreak(0);
      setFlash("wrong");
    }
    setCalls((c) => c + 1);
    setTimeout(() => {
      setFlash(null);
      setClaim(makeClaim(pack));
    }, 350);
  };

  const restart = () => {
    setScore(0); setStreak(0); setCalls(0); setRemaining(ROUND_TIME);
    setClaim(makeClaim(pack)); setDone(false); setFlash(null);
    startRef.current = Date.now();
  };

  const accuracy = calls > 0 ? Math.round((score / Math.max(1, calls)) * 100) : 0;
  const mult = useMemo(() => streakMultiplier(streak), [streak]);

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-xl w-full text-center animate-reveal">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Game V · Results</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">True or False</h1>
          <div className="my-10">
            <div className="font-display text-7xl sm:text-9xl text-gold">{score}</div>
            <div className="mt-3 font-display text-2xl">{score >= 30 ? "Lightning eye." : score >= 20 ? "Sharp ear." : score >= 10 ? "Solid." : "Try again."}</div>
            <div className="mt-3 flex justify-center gap-6 text-sm text-muted-foreground">
              <div>Calls: <span className="text-gold font-mono">{calls}</span></div>
              <div>Best: <span className="text-gold font-mono">{best}</span></div>
              <div>Longest streak: <span className="text-gold font-mono">{bestStreak}</span></div>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button onClick={restart} className="rounded-full bg-gradient-gold px-6 py-3 text-sm font-semibold text-ink">Play again</button>
            <Link to="/games/$slug" params={{ slug: "true-or-false" }} className="rounded-full border border-border px-6 py-3 text-sm">Back to hub</Link>
            <Link to="/games" className="rounded-full border border-border px-6 py-3 text-sm">All games</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GameShell title="True or False" numeral="V" score={score} round={calls + 1} total={TOTAL_CALLS} streak={streak} timeLimit={remaining > 0 ? remaining : 1} hubSlug="true-or-false">
      <div className={`rounded-2xl border-2 p-8 sm:p-12 shadow-elegant grain transition-all ${
        flash === "right" ? "border-success bg-success/10" : flash === "wrong" ? "border-error bg-error/10 animate-shake" : "border-border bg-card"
      }`}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Claim</div>
        <div className="mt-3 font-display text-xl sm:text-2xl text-gold">{claim.claimedAuthor}</div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">said:</div>
        <p className={`mt-4 font-display leading-snug ${live ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"}`}>"{claim.claimedText}"</p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <button onClick={() => answer(true)}
          className="rounded-2xl border-2 border-success/40 bg-success/10 hover:bg-success/20 text-success font-display text-3xl sm:text-5xl py-10 sm:py-14 transition active:scale-95">
          ✓ TRUE
        </button>
        <button onClick={() => answer(false)}
          className="rounded-2xl border-2 border-error/40 bg-error/10 hover:bg-error/20 text-error font-display text-3xl sm:text-5xl py-10 sm:py-14 transition active:scale-95">
          ✗ FALSE
        </button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Calls</div>
          <div className="font-mono text-lg text-gold">{calls}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Accuracy</div>
          <div className="font-mono text-lg text-gold">{accuracy}%</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Multiplier</div>
          <div className="font-mono text-lg text-gold">×{mult}</div>
        </div>
      </div>
    </GameShell>
  );
}
